import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import UserContext from "components/contexts/UserContext";
import Footer from "components/Footer";
import Header from "components/Header";
import { Loader } from "components/loaders";
import NavDrawer from "components/NavDrawer";
import RouterBreadcrumbs from "components/RouterBreadcrumbs";
import GeneralError from "components/validation/GeneralError";
import NoConnection from "components/validation/NoConnection";
import {
  ERROR_LOGOUT_INACTIVITY,
  ERROR_LOGOUT_NON_ADMIN,
} from "constants/messages";
import { DASHBOARD, LOGIN, PROTECTED } from "constants/routes";
import { IDLE_TIMEOUT } from "constants/vars";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { collection, getDocs, getFirestore } from "firebase/firestore";
import useIsMounted from "ismounted";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router";
// routes config
import routes from "routes";
import { getProfile } from "utils/slide/link/user";
import { checkIsActive, checkIsAdmin, checkIsEmailVerified } from "utils/ui";
import AuthCheck from "views/Auth/AuthCheck";
import { RequireAuth } from "views/Auth/RequireAuth";

export default function Protected() {
  const { enqueueSnackbar } = useSnackbar();
  const isMounted = useIsMounted();
  let location = useLocation();

  const auth = getAuth();
  const db = getFirestore();
  const navigate = useNavigate();

  const [isAuthed, setIsAuthed] = useState(false);
  const [userLink, setUserLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const logout = () => {
    signOut(auth);
  };

  const logoutAction = () => {
    navigate(LOGIN, { replace: true, state: { returnUrl: location.pathname } });
  };

  useEffect(() => {
    if (location && location.state && location.state.login === "success") {
      if (location.state.returnUrl === PROTECTED) {
        navigate(PROTECTED + DASHBOARD, { replace: true });
      } else {
        navigate(location.state.returnUrl, { replace: true });
      }
      enqueueSnackbar("Successfully signed in", { variant: "success" });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        getDocs(collection(db, "mfa-check"))
          .then(() => {
            setIsAuthed(true);
            loadProfile();
          })
          .catch(() => {
            logout();
          });
      } else {
        setIsAuthed(false);
        logoutAction();
      }
    });

    const loadProfile = () => {
      getProfile().then((res) => {
        if (isMounted.current) {
          if (res.status === "success") {
            if (checkIsAdmin(res.data)) {
              setUserLink(res.data);
              if (!checkIsActive(res.data.status)) {
                setError("inactive");
              } else if (!checkIsEmailVerified(res.data.email_verified)) {
                setError("email_unverified");
              }
            } else {
              enqueueSnackbar(ERROR_LOGOUT_NON_ADMIN, {
                variant: "error",
              });
              logout(auth);
            }
          } else {
            setError("general");
          }
          setLoading(false);
        }
      });
    };

    return () => unsubscribe();
    // eslint-disable-next-line
  }, []);

  const handleOnIdle = () => {
    enqueueSnackbar(ERROR_LOGOUT_INACTIVITY, { variant: "error" });
    logout();
  };

  useIdleTimer({
    timeout: IDLE_TIMEOUT * 1000,
    onIdle: handleOnIdle,
    debounce: 500,
  });

  return (
    <Box sx={{ backgroundColor: "background.default" }}>
      <UserContext.Provider
        value={{
          logout: logout,
          userLink: userLink,
          isAuthed: isAuthed,
        }}
      >
        <AuthCheck />

        <React.Fragment>
          {isAuthed && <Header />}
          {isAuthed && <NavDrawer />}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: "100vh",
              pl: isAuthed ? 7 : 0,
            }}
          >
            <Box sx={{ mt: 8 }}>{isAuthed && <RouterBreadcrumbs />}</Box>
            {(loading || error) && (
              <Container
                component="main"
                sx={{
                  mt: 4,
                  mb: 2,
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {loading && <Loader />}
                {!loading && error === "general" && <NoConnection />}
                {!loading && error === "inactive" && (
                  <GeneralError
                    title="Account Inactive"
                    message="Your account needs to be activate to use the Dashboard functionality."
                  />
                )}
                {!loading && error === "email_unverified" && (
                  <GeneralError
                    title="Email Unverified"
                    message="Please verify your email address before using the Dashboard functionality."
                  />
                )}
              </Container>
            )}
            {!loading && !error && (
              <React.Suspense fallback={<React.Fragment />}>
                <Routes>
                  {routes.map((route, idx) => {
                    return (
                      route.component && (
                        <Route
                          key={idx}
                          path={route.path}
                          element={
                            <React.Fragment>
                              {
                                <RequireAuth redirectTo={LOGIN}>
                                  <route.component />
                                </RequireAuth>
                              }
                            </React.Fragment>
                          }
                        />
                      )
                    );
                  })}
                  <Route
                    path={"*"}
                    element={<Navigate to={PROTECTED + DASHBOARD} replace />}
                  />
                </Routes>
              </React.Suspense>
            )}
            {isAuthed && <Footer />}
          </Box>
        </React.Fragment>
      </UserContext.Provider>
    </Box>
  );
}
